/**
 * Initial preference setup for a signed-in user. Collects planning settings, submits completion to
 * the user API, and updates local user state so the application can enter planner routes.
 *
 * @module apps/web/src/pages/Onboarding
 */

import { useState } from 'react';
import { Zap, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { useUserStore } from '@/stores/userStore';
import { useToast } from '@/hooks/use-toast';

/** Weekday labels paired with JavaScript weekday numbers for initial work-day selection. */
const DAYS = [
  ['Sun', 0],
  ['Mon', 1],
  ['Tue', 2],
  ['Wed', 3],
  ['Thu', 4],
  ['Fri', 5],
  ['Sat', 6],
] as const;

/**
 * Initial preference setup for a signed-in user. Collects planning settings, submits completion to
 * the user API, and updates local user state so the application can enter planner routes.
 */
export function Onboarding() {
  const { toast } = useToast();
  const setUser = useUserStore(s => s.setUser);
  const userId = useUserStore(s => s.userId);
  const email = useUserStore(s => s.email);
  const displayName = useUserStore(s => s.displayName);
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [workDays, setWorkDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [workStart, setWorkStart] = useState('09:00');
  const [workEnd, setWorkEnd] = useState('17:00');
  const [breakStart, setBreakStart] = useState('12:00');
  const [breakEnd, setBreakEnd] = useState('13:00');
  const [peakStart, setPeakStart] = useState(9);
  const [peakEnd, setPeakEnd] = useState(12);
  const [isSaving, setIsSaving] = useState(false);

  const toggleDay = (day: number) => {
    setWorkDays(days =>
      days.includes(day) ? days.filter(existing => existing !== day) : [...days, day].sort()
    );
  };

  const save = async () => {
    setIsSaving(true);
    try {
      const response = await api.user.completeOnboarding({
        timezone,
        workDays,
        workHours: { start: workStart, end: workEnd },
        breakWindow: { start: breakStart, end: breakEnd },
        peakWindows: [{ start: peakStart, end: peakEnd, energy: 9 }],
      });

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to save onboarding');
      }

      setUser({
        id: userId!,
        email: email || '',
        displayName: displayName || undefined,
        timezone,
        onboardingCompleted: true,
        settings: {
          workHours: { start: workStart, end: workEnd },
        },
      });
    } catch (error) {
      toast({
        title: 'Setup failed',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background p-6">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            Energy setup
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="timezone">Timezone</Label>
            <Input id="timezone" value={timezone} onChange={event => setTimezone(event.target.value)} />
          </div>

          <div className="space-y-3">
            <Label>Planning days</Label>
            <div className="flex flex-wrap gap-3">
              {DAYS.map(([label, day]) => (
                <label key={day} className="flex items-center gap-2 rounded-md border px-3 py-2">
                  <Checkbox checked={workDays.includes(day)} onCheckedChange={() => toggleDay(day)} />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="workStart">Work start</Label>
              <Input id="workStart" type="time" value={workStart} onChange={event => setWorkStart(event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="workEnd">Work end</Label>
              <Input id="workEnd" type="time" value={workEnd} onChange={event => setWorkEnd(event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="breakStart">Break start</Label>
              <Input id="breakStart" type="time" value={breakStart} onChange={event => setBreakStart(event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="breakEnd">Break end</Label>
              <Input id="breakEnd" type="time" value={breakEnd} onChange={event => setBreakEnd(event.target.value)} />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="peakStart">Peak energy starts</Label>
              <Input id="peakStart" type="number" min={0} max={23} value={peakStart} onChange={event => setPeakStart(Number(event.target.value))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="peakEnd">Peak energy ends</Label>
              <Input id="peakEnd" type="number" min={1} max={24} value={peakEnd} onChange={event => setPeakEnd(Number(event.target.value))} />
            </div>
          </div>

          <Button onClick={save} disabled={isSaving || workDays.length === 0} className="w-full">
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Start planning
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
